export type TopTab = 'basics' | 'points' | 'management';

export const TOP_TABS: { id: TopTab; label: string }[] = [
  { id: 'basics', label: 'Basics' },
  { id: 'points', label: 'Points' },
  { id: 'management', label: 'Manage' },
];

export const SUB_TABS: Record<TopTab, { id: string; label: string }[]> = {
  basics: [
    { id: 'overview', label: 'Overview' },
    { id: 'theBasics', label: 'The Basics' },
    { id: 'gameweeks', label: 'Gameweeks' },
    { id: 'team', label: 'Creating A Team' },
    { id: 'teamBudget', label: 'Team Budget & Player' },
    { id: 'deadlines', label: 'Gameweek Deadlines' },
  ],
  points: [
    { id: 'all-players', label: 'All Positions' },
    { id: 'gk-def', label: 'Goalkeepers & Defenders' },
    { id: 'bonus-pts', label: 'Bonus points & PPM' },
  ],
  management: [
    { id: 'transfers', label: 'Transfers' },
    { id: 'captaincy', label: 'Captaincy' },
    { id: 'boosters', label: 'Booster' },
    { id: 'substitutes', label: 'Substitutes' },
    { id: 'leagues-prizes', label: 'Leagues' },
  ],
};

export type RowIcon =
  | 'goal'
  | 'assist'
  | 'penalty-miss'
  | 'penalty-save'
  | 'yellow-card'
  | 'red-card'
  | 'own-goal';

export interface GuideRow {
  label: string;
  value?: string;
  description?: string;
}

export interface GuideSection {
  title?: string;
  paragraphs?: string[];
  items?: GuideRow[];
  rows?: GuideRow[];
  note?: string;
  collapsible?: boolean;
  sections?: GuideSection[];
}

export interface ScoringRow {
  label: string;
  note?: string;
  icon?: RowIcon;
  badge?: string;
  points: string;
}

export interface ScoringContent {
  kind: 'scoring';
  paragraphs?: string[];
  rows: ScoringRow[];
  showLearnMore?: boolean;
  ppmFooter?: boolean;
  sections?: GuideSection[];
  note?: string;
}

export interface ParagraphContent {
  kind: 'paragraph';
  paragraphs?: string[];
  sections?: GuideSection[];
}

export type ContentData = ScoringContent | ParagraphContent;

export const DEFAULT_CONTENT: Record<TopTab, Record<string, ContentData>> = {
  basics: {
    overview: {
      kind: 'paragraph',
      paragraphs: [
        'Welcome to our Season Fantasy Game. These simplified rules provide a clear, comprehensive guide on how to play, manage your team and track your scoring across the season.',
        'For complete information, see our full <a href="https://www.thesun.co.uk/administration/39908513/dream-team-2026-27-game-rules" target="_blank" rel="noopener noreferrer">Game Rules</a>. To enter and win prizes, you must meet the eligibility criteria set out in Section 2.1 of the <a href="https://www.thesun.co.uk/administration/39908478/dream-team-2026-27-terms-conditions/" target="_blank" rel="noopener noreferrer">General Terms & Conditions</a>.',
      ],
    },
    theBasics: {
      kind: 'paragraph',
      paragraphs: [
        'Our Season Fantasy Game follows the classic Dream Team format.',
        'Using a starting budget of £50m, you must build a team of 11 players selected from the 20 English Premier League clubs.',
        'Points are accumulated each Gameweek based on the performances of the players in your team during domestic and European competitions.',
        'Each Gameweek you’re given two transfers to refresh your team, or bank for future weeks.',
      ],
    },
    gameweeks: {
      kind: 'paragraph',
      paragraphs: [
        'The season is structured around scoring periods known as Gameweeks. These typically start on a Friday evening or Saturday lunchtime, depending on Premier League and FA Cup schedules. Your players can compete in up to two matches in a Gameweek.',
      ],
    },
    team: {
      kind: 'paragraph',
      sections: [
        {
          paragraphs: [
            'You must select a team of 11 players from the available pool using a starting budget of £50m.',
          ],
          rows: [
            {
              label: 'Dynamic formations',
              description:
                "You'll be asked to choose an initial formation, but it will adapt dynamically based on the players you pick. Seven eligible formations are available: 4-4-2, 4-3-3, 4-5-1, 3-4-3, 3-5-2, 5-4-1, and 5-3-2.",
            },
            {
              label: 'Club restriction',
              description:
                'There is no restriction on the number of players you can select from a single club.',
            },
            {
              label: 'Captain & Vice-captain',
              description:
                "You must nominate a Captain, who will score double points, and a Vice-captain who will score double if your Captain doesn't play.",
            },
            {
              label: 'Team names',
              description:
                'Limited to 25 characters. Offensive or inappropriate names may be automatically changed or lead to team disqualification.',
            },
          ],
        },
      ],
    },
    teamBudget: {
      kind: 'paragraph',
      paragraphs: [
        'Your initial transfer budget is £50m.',
        'Player prices will change (rising or falling £0.1m, £0.2m or £0.3m, or staying the same) based on how they perform, meaning your overall budget will adjust as the season progresses.',
        'Prices are adjusted when a new Gameweek begins, after transfers are completed. Predicting changes and building budget is a key part of the game.',
      ],
    },
    deadlines: {
      kind: 'paragraph',
      paragraphs: [
        '<b>Gameweek 1 lock:</b> If you create a team before the Fantasy Season has commenced, unlimited transfers can be made until the start of Gameweek 1 (GW1) at 8pm BST on 21 August 2026.',
        '<b>Subsequent deadlines:</b> Beyond GW1, the standard cut-off to make changes is 90 minutes before kick-off in the first match of each Gameweek.',
      ],
    },
  },

  points: {
    'all-players': {
      kind: 'scoring',
      paragraphs: [
        'Points are calculated using live match data provided by OPTA. Scoring includes normal time and extra time (where applicable), but excludes penalty shoot-outs.',
      ],
      rows: [
        { label: 'Goal Scored (Normal Play/Penalty)', icon: 'goal', points: '+6' },
        { label: 'Assist / Fantasy Goal Assist', icon: 'assist', points: '+3' },
        { label: 'Shot on Target', points: '+1' },
        { label: 'Big Chance Created', points: '+1' },
        { label: 'Tackles', note: '  |  per 2 tackles', points: '+1' },
        { label: 'Appearance', note: '  |  up to 60 minutes', points: '+1' },
        { label: 'Appearance', note: '  |  60 minutes or more', points: '+1' },
        { label: 'Yellow Card', icon: 'yellow-card', points: '-1' },
        { label: 'Red Card (or two yellows)', icon: 'red-card', points: '-3' },
        { label: 'Penalty Miss', icon: 'penalty-miss', points: '-3' },
        { label: 'Own Goal', icon: 'own-goal', points: '-2' },
      ],
      note: 'If a player receives two Yellow Cards he will be docked -3 for both.',
    },
    'gk-def': {
      kind: 'scoring',
      rows: [
        { badge: 'GK', label: 'Saves', note: '  |  per 2 saves', points: '+1' },
        { badge: 'GK', label: 'Penalty Save', icon: 'penalty-save', points: '+3' },
        {
          badge: 'GK, DEF',
          label: 'Clean Sheet',
          note: '  |  Minimum 60 minutes played',
          points: '+5',
        },
        { badge: 'GK, DEF', label: '2 Goals Conceded', points: '-1' },
        { badge: 'GK, DEF', label: 'Each Goal Conceded Over 2 Goals', points: '-1' },
      ],
      note: 'Defenders and Goalkeepers who receive a red card will continue to be penalised for any goals their team concedes after they leave the pitch.',
    },
    'bonus-pts': {
      kind: 'scoring',
      paragraphs: [
        "Bonus points are added to a player's Gameweek total based on the Player Performance Marks (PPMs) they accumulate during a match:",
      ],
      rows: [
        { label: '4 or lower', points: '0' },
        { label: '5 - 7', points: '+1' },
        { label: '8 - 11', points: '+3' },
        { label: '12 or higher', points: '+5' },
      ],
      sections: [
        {
          title: 'PPM Metric Breakdown',
          paragraphs: ['All players'],
          rows: [
            { label: 'Successful Dribble', value: '+1' },
            { label: 'Accurate Cross', value: '+1' },
            { label: 'Interception', value: '+1' },
            { label: 'Block', value: '+1' },
            { label: 'Goal Outside the Penalty Area', value: '+1' },
            { label: 'Foul Won', value: '+1' },
            { label: 'Pass Completion Rate (70-79%)', value: '+1' },
            { label: 'Pass Completion Rate (80-89%)', value: '+2' },
            { label: 'Pass Completion Rate (90% or higher)', value: '+3' },
            { label: 'Offside', value: '-1' },
            { label: 'Foul Conceded', value: '-1' },
            { label: 'Error Leading to Goal', value: '-2' },
          ],
          sections: [
            {
              paragraphs: [
                '* Note: Players must attempt a minimum of 25 passes in a match to qualify for Pass Completion PPMs.',
              ],
            },
            {
              paragraphs: ['Goalkeepers'],
              rows: [
                { label: 'Successful Claim', value: '+1' },
                { label: 'Successful Punch', value: '+1' },
                { label: 'Keeper Sweep (rushing off line under pressure)', value: '+1' },
              ],
            },
          ],
          collapsible: true,
        },
      ],
    },
  },

  management: {
    transfers: {
      kind: 'paragraph',
      sections: [
        {
          rows: [
            {
              label: 'Transfer allocation',
              description:
                "You are given unlimited transfers ahead of the first Gameweek (if desired, you can create a team, save it and then update it later). After that, you're allocated two transfers ahead of each new Gameweek.",
            },
            {
              label: 'Rollovers',
              description:
                'Unused transfers will be rolled over (up to a maximum of four) allowing up to six transfers to use in a single week.',
            },
          ],
        },
      ],
    },
    captaincy: {
      kind: 'paragraph',
      sections: [
        {
          rows: [
            {
              label: 'Double points',
              description:
                'Your nominated Captain will earn double points for each eligible match they play in during the next Gameweek. Any negative scores are also doubled (eg a score of -3 becomes -6).',
            },
            {
              label: 'Vice-captain activation',
              description:
                'If your selected Captain does not play in the relevant Gameweek, then your selected Vice-Captain will instead be awarded with double points.',
            },
          ],
        },
      ],
    },
    boosters: {
      kind: 'paragraph',
      paragraphs: [
        'You have access to three tactical Boosters, which can each be deployed once during the season. Only one Booster can be active during a Gameweek, and activating a Booster deactivates the Substitute feature.',
      ],
      sections: [
        {
          rows: [
            {
              label: 'Goal Bonus',
              description:
                'Every goal scored by your players awards 9 points instead of 6. A captained player scores 18 points per goal.',
            },
            {
              label: '12th Man',
              description:
                'Temporarily add a 12th player of any value or position to your squad for a Gameweek. The 12th Man cannot be captained.',
            },
            {
              label: 'Max Captain',
              description:
                'Disables the normal captain features. At the end of the Gameweek, your highest-scoring player is retrospectively made Captain and receives double points.',
            },
          ],
        },
      ],
    },
    substitutes: {
      kind: 'paragraph',
      paragraphs: [
        'You can perform a mid-Gameweek substitution up to <b>10 times</b> across the season, capped at a maximum of one substitution per Gameweek - but only if you do not have a Booster active.',
      ],
      sections: [
        {
          rows: [
            {
              label: 'Availability',
              description:
                'The Substitute option becomes available if one or more of your players does not compete for a single minute in their team’s completed fixture. You can only swap them out for a player in the active competition who has played zero minutes across that competition.',
            },
            {
              label: 'Exception',
              description: 'A substitution cannot be made if a Booster is active.',
            },
            {
              label: 'Timing and value',
              description:
                'Substitutions can be executed while the Gameweek substitution window is open. The incoming player must be of equal or lesser value than the outgoing player plus any budget you have.',
            },
            {
              label: 'Captaincy impact',
              description:
                'If you substitute out your Captain, the incoming player automatically inherits the armband and scores double points. Making a substitution completely deactivates the Vice-Captain feature for that Gameweek.',
            },
            {
              label: 'No reversal',
              description: 'Once confirmed, a substitution cannot be undone.',
            },
            {
              label: 'Reset',
              description:
                'A substitution is temporary. Your team will automatically reset to its original state before the next Gameweek starts.',
            },
          ],
        },
      ],
    },
    'leagues-prizes': {
      kind: 'paragraph',
      sections: [
        {
          rows: [
            {
              label: 'Leaderboards',
              description:
                'You are automatically entered into the main Fantasy Season Leaderboard, as well as Club and National leaderboards. Prizes are awarded to managers who finish in the top 10 positions in the Leaderboard over the entire Fantasy Season. There are also weekly prizes up for grabs. ',
            },
            {
              label: 'Mini Leagues',
              description:
                'You can join or create Mini Leagues to play against family, friends and colleagues. Note that there are no official prizes or rewards associated with Mini Leagues.',
            },
          ],
        },
      ],
    },
  },
};
